import { getMbFileLimit } from '@payaca/helpers/fileHelper';
import { UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP } from '@payaca/types/uploadTypes';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import Alert from '../Alert/Alert';
import UntitledLoading from '../UntitledLoading/UntitledLoading';
import FileDropIcon from './FileDropIcon.svg?react';

export type AcceptedFiles = {
  extensions: string[];
  /** A summary to show in place of the extensions, e.g. "images and documents". */
  summary?: string;
};

export type FileDropZoneProps = {
  className?: string;
  isLoading?: boolean;
  hasError?: boolean;
  accepts?: AcceptedFiles;
  dropzoneOptions?: Omit<DropzoneOptions, 'accept'>;
  remainingSlots?: number;
};

/**
 * Where someone drops or picks files to upload.
 *
 * ## Declaring what it accepts
 *
 * `accepts.extensions` filters the picker, and is the list named back to
 * someone whose file is rejected. Pass a `summary` too once that list is long
 * enough to stop being readable — the zone then shows the summary at rest and
 * names the extensions only on rejection.
 *
 * `@payaca/uploads` builds both halves from a set of upload categories:
 *
 * ```tsx
 * <FileDropZone accepts={acceptedFilesForCategories(['image', 'document'])} />
 * ```
 */

const FileDropZone: FC<FileDropZoneProps> = (props) => {
  const {
    className,
    isLoading = false,
    hasError = false,
    accepts,
    dropzoneOptions,
    remainingSlots,
  } = props;

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      disabled: dropzoneOptions?.disabled || isLoading,
      maxFiles: remainingSlots,
      ...dropzoneOptions,
      accept: accepts?.extensions,
    });

  const rejectionError = fileRejections.find((rejection) =>
    rejection.errors.some((error) => error.code === 'too-many-files')
  );

  const hasInvalidTypeRejection = fileRejections.some((rejection) =>
    rejection.errors.some((error) => error.code === 'file-invalid-type')
  );

  const tooLargeFileNames = fileRejections
    .filter((rejection) =>
      rejection.errors.some((error) => error.code === 'file-too-large')
    )
    .map((rejection) => rejection.file.name);

  // Whatever the caller enforces is what we tell people, so the two can never
  // disagree. Without `maxSize` the zone falls back to the legacy default,
  // which is lower than a direct-to-storage upload actually allows.
  const maxSize = dropzoneOptions?.maxSize ?? UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP;

  const fileTypesCleaned = accepts?.extensions.length
    ? accepts.extensions
        .map((extension) => extension.replace(/^\./, '').toUpperCase())
        .join(', ')
    : null;

  const acceptedFilesHint = accepts?.summary
    ? `Accepts ${accepts.summary}`
    : fileTypesCleaned;

  return (
    <div
      className={clstx(
        'relative m-auto cursor-pointer rounded-xl border border-dashed p-10',
        isDragActive
          ? 'border-blue-600 bg-blue-50'
          : 'border-gray-300 bg-white',
        className
      )}
      {...getRootProps()}
    >
      <div
        className={
          'typography:prose flex flex-col items-center text-center' +
          (isLoading ? ' invisible' : '')
        }
      >
        <input {...getInputProps()} />

        {hasError && (
          <Alert className="mb-4" colour="red">
            An error occurred - please try again later
          </Alert>
        )}

        {rejectionError && (
          <Alert className="mb-4" colour="red">
            You can only select a maximum of {dropzoneOptions?.maxFiles} files
            at once.
          </Alert>
        )}

        {hasInvalidTypeRejection && (
          <Alert className="mb-4 w-full min-w-0 wrap-anywhere" colour="red">
            That file type isn&rsquo;t accepted
            {fileTypesCleaned ? `. Supported: ${fileTypesCleaned}` : ''}
          </Alert>
        )}

        {tooLargeFileNames.length > 0 && (
          <Alert className="mb-4 w-full min-w-0 wrap-anywhere" colour="red">
            {tooLargeFileNames.length === 1
              ? 'This file is'
              : 'These files are'}{' '}
            larger than the {getMbFileLimit(maxSize)} limit:{' '}
            {tooLargeFileNames.join(', ')}
          </Alert>
        )}

        <FileDropIcon className="mb-4" />
        <h4 className="mb-1">
          Drop your file
          {dropzoneOptions?.maxFiles === 1 ? '' : 's'} here or{' '}
          <span className="link-body">browse</span>
        </h4>
        {!!acceptedFilesHint && (
          <p className="supporting-body text-gray-500 mb-1">
            {acceptedFilesHint}
          </p>
        )}
        <p className="supporting-body text-gray-500">
          Maximum size: {getMbFileLimit(maxSize)}
        </p>
      </div>

      {isLoading ? (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
          role="status"
        >
          <UntitledLoading className="h-6 w-6" />
          <p className="supporting-body text-gray-500">Uploading…</p>
        </div>
      ) : null}
    </div>
  );
};

export default FileDropZone;
